import { FiltersType, OfferType, Unit, UnitsMultilevelList } from '@/types';

type FlatUnit = Unit & { children?: FlatUnit[] };

function findUnit(list: FlatUnit[], code: string): Unit | null {
  for (const item of list) {
    if (item.code === code) return item;
    if (item.children?.length) {
      const found = findUnit(item.children, code);
      if (found) return found;
    }
  }
  return null;
}

export type ActiveFilterChip = {
  id: string;
  label: string;
  patch: Partial<FiltersType>;
};

export const getActiveFilterChips = (
  filters: FiltersType,
  unitList?: UnitsMultilevelList | null,
): ActiveFilterChip[] => {
  const chips: ActiveFilterChip[] = [];
  const list = (unitList ?? []) as FlatUnit[];

  filters.categoriesValue?.forEach((c) => {
    chips.push({
      id: `cat-${c.code}`,
      label: c.title,
      patch: {
        categoriesValue: filters.categoriesValue.filter((x) => x.code !== c.code),
        currentPage: 0,
      },
    });
  });

  filters.equipmentValue?.forEach((e) => {
    chips.push({
      id: `equip-${e.code}`,
      label: e.title,
      patch: {
        equipmentValue: filters.equipmentValue.filter((x) => x.code !== e.code),
        currentPage: 0,
      },
    });
  });

  filters.unitListValue?.forEach((code) => {
    const u = findUnit(list, code);
    chips.push({
      id: `unit-${code}`,
      label: u?.title ?? code,
      patch: {
        unitListValue: filters.unitListValue.filter((c) => c !== code),
        currentPage: 0,
      },
    });
  });

  if (filters.areaValue?.length === 2) {
    chips.push({
      id: 'area',
      label: `${filters.areaValue[0]}–${filters.areaValue[1]} m²`,
      patch: { areaValue: [], currentPage: 0 },
    });
  }
  if (filters.roomNumberValue?.length === 2) {
    chips.push({
      id: 'rooms',
      label: `${filters.roomNumberValue[0]}–${filters.roomNumberValue[1]} Zimmer`,
      patch: { roomNumberValue: [], currentPage: 0 },
    });
  }
  if (filters.offerTypeValue === OfferType.SELL && filters.priceValue?.length === 2) {
    chips.push({
      id: 'price',
      label: `bis € ${filters.priceValue[1].toLocaleString('de-DE')}`,
      patch: { priceValue: [], currentPage: 0 },
    });
  }
  if (filters.offerTypeValue === OfferType.RENT && filters.rentPriceValue?.length === 2) {
    chips.push({
      id: 'rentPrice',
      label: `bis € ${filters.rentPriceValue[1].toLocaleString('de-DE')}`,
      patch: { rentPriceValue: [], currentPage: 0 },
    });
  }
  if (filters.powerConsumptionClasses?.length) {
    chips.push({
      id: 'hwbClass',
      label: `HWB: ${filters.powerConsumptionClasses.join(', ')}`,
      patch: { powerConsumptionClasses: [], currentPage: 0 },
    });
  }
  if (filters.energyClassFactors?.length) {
    chips.push({
      id: 'fgeeClass',
      label: `FGEE: ${filters.energyClassFactors.join(', ')}`,
      patch: { energyClassFactors: [], currentPage: 0 },
    });
  }
  filters.locations?.forEach((loc) => {
    let label: string;
    if (loc.type === 'ZIP') {
      label = loc.name;
    } else if (loc.type === 'PLACE' && loc.postcode) {
      label = `${loc.postcode} ${loc.name}`;
    } else {
      label = loc.foundBy ? `${loc.name} ${loc.foundBy}` : loc.name;
    }
    const id =
      loc.type === 'PLACE'
        ? `loc-${loc.type}-${loc.code}-${loc.postcode ?? ''}`
        : `loc-${loc.type}-${loc.code}`;
    chips.push({
      id,
      label,
      patch: {
        locations: filters.locations!.filter((l) => {
          if (l.type !== loc.type || l.code !== loc.code) return true;
          if (loc.type === 'PLACE') return l.postcode !== loc.postcode;
          return false;
        }),
        currentPage: 0,
      },
    });
  });

  return chips;
};
